import type { ReactNode } from "react";
import styles from "./Donut.module.css";
import type { DonutTone } from "@/types";

const VIEWBOX_UNITS_PER_EM = 100;

export type DonutGeometry = {
  viewBoxSize: number;
  center: number;
  radius: number;
  innerRadius: number;
  strokeWidth: number;
};

type DonutProps = {
  value: number;
  min?: number;
  max?: number;
  size?: number;
  strokeWidth?: number;
  tone: DonutTone;
  title: string;
  isAnimated?: boolean;
  children?: ReactNode | ((geometry: DonutGeometry) => ReactNode);
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const Donut = ({ value, min = 0, max = 10, size = 6, strokeWidth = 8, tone, title, isAnimated = false, children }: DonutProps) => {
  const viewBoxSize = size * VIEWBOX_UNITS_PER_EM;
  const center = viewBoxSize / 2;
  const boundedValue = Number.isFinite(value) ? clamp(value, min, max) : min;
  const progress = max === min ? 0 : (boundedValue - min) / (max - min);
  const radius = (viewBoxSize - strokeWidth) / 2;
  const innerRadius = radius - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - progress);
  const showProgress = progress > 0;
  const geometry = { viewBoxSize, center, radius, innerRadius, strokeWidth };
  const content = typeof children === "function" ? children(geometry) : children;

  return (
    <div className={[styles.root, styles[`tone_${tone}`], isAnimated && styles.isAnimating].filter(Boolean).join(" ")}>
      <svg aria-label={title} className={styles.svg} role='img' viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`} width={`${size}em`} height={`${size}em`}>
        <title>{title}</title>
        <circle className={styles.track} cx={center} cy={center} r={radius} strokeWidth={strokeWidth} />
        {showProgress && (
          <circle
            className={[styles.progress, styles.rotated].join(" ")}
            cx={center}
            cy={center}
            r={radius}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            strokeLinecap={progress >= 1 ? "butt" : "round"}
          />
        )}
        {content}
      </svg>
    </div>
  );
};

export default Donut;
